import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private readonly _translate = inject(TranslateService);
	basePath: string = environment.API_URL;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	headers: { [key: string]: string } = { 'Content-Type': 'application/json' };
	httpOptions: HttpOptions = {
		headers: new HttpHeaders(this.headers),
		withCredentials: true,
	};

	/** Per-request options carrying the current UI language so the backend localizes prose. */
	protected reqOpts(): HttpOptions {
		return {
			headers: new HttpHeaders({
				...this.headers,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				'Accept-Language':
					this._translate.currentLang || this._translate.defaultLang || 'en',
			}),
			withCredentials: true,
		};
	}

	constructor(private http: HttpClient) {}

	handleError(error: HttpErrorResponse): Observable<never> {
		// 401 handling is done by authInterceptor (token refresh → forceLogout → /login).
		// Do NOT navigate here to avoid a race condition where noAuthGuard redirects
		// an apparently-authenticated user to /dashboard before the interceptor clears state.
		return throwError(() => error);
	}

	sendPOST<T>(path: string, postData: object): Observable<T> {
		return this.http
			.post<T>(this.basePath + path, postData, this.reqOpts())
			.pipe(catchError((error) => this.handleError(error)));
	}

	sendPUT<T>(path: string, body: object): Observable<T> {
		return this.http
			.put<T>(this.basePath + path, body, this.reqOpts())
			.pipe(catchError((error) => this.handleError(error)));
	}

	sendGET<T>(path: string, basePath: string = null): Observable<T> {
		return this.http
			.get<T>((basePath != null ? basePath : this.basePath) + path, this.reqOpts())
			.pipe(catchError((error) => this.handleError(error)));
	}

	/** GET returning a raw Blob (file downloads such as CSV export). */
	sendGETBlob(path: string): Observable<Blob> {
		return this.http
			.get(this.basePath + path, {
				headers: this.reqOpts().headers,
				withCredentials: true,
				responseType: 'blob',
			})
			.pipe(catchError((error) => this.handleError(error)));
	}

	/** POST returning a raw Blob (e.g. rendered image previews). */
	sendPOSTBlob(path: string, body: object): Observable<Blob> {
		return this.http
			.post(this.basePath + path, body, {
				headers: this.reqOpts().headers,
				withCredentials: true,
				responseType: 'blob',
			})
			.pipe(catchError((error) => this.handleError(error)));
	}

	sendDELETE<T>(path: string): Observable<T> {
		return this.http
			.delete<T>(this.basePath + path, this.reqOpts())
			.pipe(catchError((error) => this.handleError(error)));
	}

	sendPATCH<T>(path: string, body: object): Observable<T> {
		return this.http
			.patch<T>(this.basePath + path, body, this.reqOpts())
			.pipe(catchError((error) => this.handleError(error)));
	}
}

export interface HttpOptions {
	headers: HttpHeaders;
	withCredentials: boolean;
}
